import React, { useState, useEffect } from 'react'
import { User, BankInfoFormData } from '../../../types'
import { X, Building2 } from 'lucide-react'

interface BankInfoModalProps {
  user: User
  onSubmit: (data: BankInfoFormData) => void
  onCancel: () => void
  isLoading: boolean
}

const BankInfoModal: React.FC<BankInfoModalProps> = ({
  user,
  onSubmit,
  onCancel,
  isLoading
}) => {
  const [formData, setFormData] = useState<BankInfoFormData>({
    bankName: '',
    branchName: '',
    branchCode: '',
    accountNumber: '',
    accountHolder: ''
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // 如果用戶已有銀行資訊，填入現有資料
  useEffect(() => {
    if (user.bankInfo) {
      setFormData({
        bankName: user.bankInfo.bankName,
        branchName: user.bankInfo.branchName,
        branchCode: user.bankInfo.branchCode || '',
        accountNumber: user.bankInfo.accountNumber,
        accountHolder: user.bankInfo.accountHolder
      })
    }
  }, [user.bankInfo])

  const handleInputChange = (field: keyof BankInfoFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // 清除該欄位的錯誤
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.bankName.trim()) {
      newErrors.bankName = '銀行名稱為必填欄位'
    }

    if (!formData.branchName.trim()) {
      newErrors.branchName = '分行名稱為必填欄位'
    }

    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = '帳戶號碼為必填欄位'
    }

    if (!formData.accountHolder.trim()) {
      newErrors.accountHolder = '帳戶戶名為必填欄位'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    onSubmit(formData)
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-lg w-full">
        {/* 標題 */}
        <div className="flex items-center justify-between p-6 border-b border-primary-200">
          <div className="flex items-center space-x-3">
            <Building2 className="w-6 h-6 text-primary-600" />
            <h2 className="text-xl font-semibold text-secondary-800">管理銀行匯款資訊</h2>
          </div>
          <button
            onClick={onCancel}
            className="text-secondary-400 hover:text-secondary-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* 表單內容 */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* 用戶資訊 */}
          <div className="bg-primary-50 rounded-lg p-4">
            <p className="text-sm text-secondary-700">
              <span className="font-medium">成員:</span> {user.name} ({user.email})
            </p>
          </div>

          {/* 銀行資訊 */}
          <div>
            <h3 className="text-lg font-medium text-secondary-800 mb-4">銀行資訊</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 銀行名稱 */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  銀行名稱 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.bankName}
                  onChange={(e) => handleInputChange('bankName', e.target.value)}
                  className={`input-primary ${errors.bankName ? 'input-error' : ''}`}
                  placeholder="台灣銀行"
                />
                {errors.bankName && (
                  <p className="text-red-500 text-sm mt-1">{errors.bankName}</p>
                )}
              </div>

              {/* 分行名稱 */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  分行名稱 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.branchName}
                  onChange={(e) => handleInputChange('branchName', e.target.value)}
                  className={`input-primary ${errors.branchName ? 'input-error' : ''}`}
                  placeholder="信義分行"
                />
                {errors.branchName && (
                  <p className="text-red-500 text-sm mt-1">{errors.branchName}</p>
                )}
              </div>

              {/* 帳戶號碼 */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  帳戶號碼 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.accountNumber}
                  onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                  className={`input-primary ${errors.accountNumber ? 'input-error' : ''}`}
                  placeholder="123-456-789-1234"
                />
                {errors.accountNumber && (
                  <p className="text-red-500 text-sm mt-1">{errors.accountNumber}</p>
                )}
              </div>

              {/* 帳戶戶名 */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  帳戶戶名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.accountHolder}
                  onChange={(e) => handleInputChange('accountHolder', e.target.value)}
                  className={`input-primary ${errors.accountHolder ? 'input-error' : ''}`}
                  placeholder="張小明"
                />
                {errors.accountHolder && (
                  <p className="text-red-500 text-sm mt-1">{errors.accountHolder}</p>
                )}
              </div>
            </div>
          </div>

          {/* 警告訊息 */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <div className="w-5 h-5 text-yellow-600 mt-0.5">⚠️</div>
              <p className="text-sm text-yellow-800">
                請確認銀行資訊正確，這將用於薪資轉帳
              </p>
            </div>
          </div>

          {/* 操作按鈕 */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-primary-200">
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary"
              disabled={isLoading}
            >
              取消
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="loading-spinner w-4 h-4"></div>
                  <span>儲存中...</span>
                </div>
              ) : (
                '儲存資訊'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default BankInfoModal
